<script lang="ts">
    import type { Drink, Category, Tag } from "./apiClient";
    import { apiClient } from "./apiClient";
    import {
        drinks,
        categories,
        tags,
        loading,
        error,
        success,
    } from "./stores";

    export let drink: Drink | null = null;
    export let isEditing = false;
    export let onCancel: () => void;
    export let onSave: () => void;

    let name = "";
    let selectedCategories: number[] = [];
    let selectedTags: number[] = [];

    // Use a separate variable to track the drink ID to avoid reactive loops
    let currentDrinkId: number | null = null;

    $: if (drink?.id !== currentDrinkId) {
        currentDrinkId = drink?.id || null;
        if (drink) {
            name = drink.name;
            selectedCategories = drink.categories?.map((c) => c.id) || [];
            selectedTags = drink.tags?.map((t) => t.id) || [];
        } else {
            name = "";
            selectedCategories = [];
            selectedTags = [];
        }
    }

    async function handleSubmit() {
        if (!name.trim()) {
            error.set("Drink name is required");
            return;
        }

        try {
            loading.set(true);
            error.set(null);

            const drinkData = {
                name: name.trim(),
                category_ids: selectedCategories,
                tag_ids: selectedTags,
            };

            if (isEditing && drink) {
                await apiClient.updateDrink(drink.id, drinkData);
                success.set("Drink updated successfully");
            } else {
                await apiClient.createDrink(drinkData);
                success.set("Drink created successfully");
            }

            onSave();
        } catch (err) {
            error.set(
                err instanceof Error ? err.message : "Failed to save drink",
            );
        } finally {
            loading.set(false);
        }
    }

    function handleCategoryChange(categoryId: number, checked: boolean) {
        if (checked) {
            selectedCategories = [...selectedCategories, categoryId];
        } else {
            selectedCategories = selectedCategories.filter(
                (id) => id !== categoryId,
            );
        }
    }

    function handleTagChange(tagId: number, checked: boolean) {
        if (checked) {
            selectedTags = [...selectedTags, tagId];
        } else {
            selectedTags = selectedTags.filter((id) => id !== tagId);
        }
    }
</script>

<div class="section">
    <h2>{isEditing ? "Edit Drink" : "Add New Drink"}</h2>

    <form on:submit|preventDefault={handleSubmit}>
        <div class="form-group">
            <label for="drink-name">Name</label>
            <input
                id="drink-name"
                type="text"
                bind:value={name}
                placeholder="Enter drink name"
                required
            />
        </div>

        {#if $categories.length > 0}
            <div class="form-group">
                <fieldset>
                    <legend>Categories</legend>
                    <div class="checkbox-group">
                        {#each $categories as category}
                            <div class="checkbox-item">
                                <input
                                    type="checkbox"
                                    id="category-{category.id}"
                                    checked={selectedCategories.includes(
                                        category.id,
                                    )}
                                    on:change={(e) =>
                                        handleCategoryChange(
                                            category.id,
                                            e.currentTarget.checked,
                                        )}
                                />
                                <label for="category-{category.id}"
                                    >{category.name}</label
                                >
                            </div>
                        {/each}
                    </div>
                </fieldset>
            </div>
        {/if}

        {#if $tags.length > 0}
            <div class="form-group">
                <fieldset>
                    <legend>Tags</legend>
                    <div class="checkbox-group">
                        {#each $tags as tag}
                            <div class="checkbox-item">
                                <input
                                    type="checkbox"
                                    id="tag-{tag.id}"
                                    checked={selectedTags.includes(tag.id)}
                                    on:change={(e) =>
                                        handleTagChange(
                                            tag.id,
                                            e.currentTarget.checked,
                                        )}
                                />
                                <label for="tag-{tag.id}">{tag.name}</label>
                            </div>
                        {/each}
                    </div>
                </fieldset>
            </div>
        {/if}

        <div class="actions">
            <button type="submit" class="btn btn-primary" disabled={$loading}>
                {$loading
                    ? "Saving..."
                    : isEditing
                      ? "Update Drink"
                      : "Save Drink"}
            </button>
            <button type="button" class="btn btn-secondary" on:click={onCancel}>
                Cancel
            </button>
        </div>
    </form>
</div>
